/**
 * ModuleDropdown Component - TopBar MVP
 *
 * @version 1.1
 * @updated 2026-04-11
 * @module core/layouts/components
 *
 * @description
 * Dropdown compacto de selección de módulo para el TopBar MVP.
 * Incluye acciones secundarias por módulo (estadísticas, visibilidad, configuración).
 *
 * @remarks
 * **Diseño MVP v1.1**:
 * - Botón compacto: [Ícono] Nombre del módulo ▼
 * - Dropdown con búsqueda instantánea
 * - Categorías: Infraestructura, Transporte, Ambiente y Ciudad
 * - Botón de acciones (⋯) en cada módulo
 * - Menú contextual con: Estadísticas, Visibilidad, Configuración
 */

import { useState, useRef, useEffect, type KeyboardEvent, type ChangeEvent, type MouseEvent } from 'react';
import { useModuleStore } from '../../module-store';
import { useModuleVisibilityStore } from '../../module-store/module-visibility-store';
import { useModuleStatsStore } from '../../module-store/module-stats-store';
import { useEditLock } from '../EditLockContext';
import { useProject } from '../../projects';
import { ModuleConfigModal } from './ModuleConfigModal';
import type { ModuleId } from '../../module-store/types';
import './module-dropdown.css';

/**
 * Iconos SVG para módulos
 */
const MODULE_ICONS: Record<ModuleId, JSX.Element> = {
  transit: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="2" width="6" height="18" rx="1" />
      <circle cx="12" cy="6" r="1.5" />
      <circle cx="12" cy="11" r="1.5" />
      <circle cx="12" cy="16" r="1.5" />
      <path d="M9 20H15M12 20V22" />
    </svg>
  ),
  transport: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="6" width="16" height="10" rx="2" />
      <path d="M7 9H11M13 9H17" />
      <circle cx="7" cy="18" r="1.5" />
      <circle cx="17" cy="18" r="1.5" />
      <path d="M4 11V16" />
    </svg>
  ),
  roadways: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 17L9 7L15 13L21 7" />
      <path d="M3 17H21" />
      <path d="M12 17V7" strokeDasharray="2 2" />
    </svg>
  ),
  environmental: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2V4M12 20V22M4.93 4.93L6.34 6.34M17.66 17.66L19.07 19.07M2 12H4M20 12H22M4.93 19.07L6.34 17.66M17.66 6.34L19.07 4.93" />
      <path d="M12 9C10.3 9 9 10.3 9 12C9 13.7 10.3 15 12 15C13.7 15 15 13.7 15 12" />
    </svg>
  ),
  infrastructure: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="10" y="4" width="4" height="16" rx="1" />
      <rect x="8" y="6" width="8" height="10" rx="1" />
      <circle cx="12" cy="8" r="1" />
      <circle cx="12" cy="11" r="1" />
      <circle cx="12" cy="14" r="1" />
      <rect x="8" y="20" width="8" height="1" />
    </svg>
  ),
  urban: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="10" width="7" height="12" />
      <rect x="14" y="6" width="7" height="16" />
      <path d="M6 14H7M6 18H7M17 10H18M17 14H18M17 18H18" />
    </svg>
  ),
  social: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" />
      <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" />
    </svg>
  ),
  services: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" />
    </svg>
  ),
  safety: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" />
      <path d="M9 12L11 14L15 10" />
    </svg>
  ),
  waste: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6H5H21" />
      <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" />
      <path d="M10 11V17M14 11V17" />
    </svg>
  ),
  'urban-network': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="4.5" cy="4.5" r="2" fill="currentColor" />
      <circle cx="19.5" cy="4.5" r="2" fill="currentColor" />
      <circle cx="12" cy="12" r="2" fill="currentColor" />
      <circle cx="4.5" cy="19.5" r="2" fill="currentColor" />
      <circle cx="19.5" cy="19.5" r="2" fill="currentColor" />
      <line x1="6.3" y1="5.2" x2="10.2" y2="10.8" />
      <line x1="13.8" y1="10.8" x2="17.7" y2="5.2" />
      <line x1="10.2" y1="13.2" x2="6.3" y2="18.8" />
      <line x1="17.7" y1="18.8" x2="13.8" y2="13.2" />
      <line x1="6.5" y1="4.5" x2="17.5" y2="4.5" />
    </svg>
  ),
  mobility: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="2" />
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  'urban-inventory': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9H21M9 21V9" />
      <circle cx="15" cy="15" r="2" />
    </svg>
  ),
};

/**
 * Definición de módulos con categorías
 */
interface ModuleDefinition {
  id: ModuleId;
  name: string;
  shortName: string;
  description: string;
  category: 'core' | 'infrastructure' | 'transport' | 'environment';
  color: string;
}

const MODULE_DEFINITIONS: ModuleDefinition[] = [
  { id: 'urban-inventory', name: 'Inventariar', shortName: 'Inv', description: 'Árboles, luminarias, contenedores y más', category: 'core', color: '#0EA5E9' },
  { id: 'urban-network', name: 'Red Urbana', shortName: 'Red', description: 'Red vial canónica y topología', category: 'infrastructure', color: '#64748B' },
  { id: 'roadways', name: 'Vialidades', shortName: 'Vías', description: 'Segmentos de calzada y defectos', category: 'infrastructure', color: '#64748B' },
  { id: 'infrastructure', name: 'Infraestructura', shortName: 'Infra', description: 'Alumbrado y mobiliario urbano', category: 'infrastructure', color: '#F59E0B' },
  { id: 'services', name: 'Servicios', shortName: 'Serv', description: 'Agua, cloacas, gas, electricidad', category: 'infrastructure', color: '#14B8A6' },
  { id: 'transit', name: 'Tránsito', shortName: 'Trán', description: 'Semáforos y señalización', category: 'transport', color: '#EF4444' },
  { id: 'transport', name: 'Transporte', shortName: 'Trans', description: 'Transporte público y rutas', category: 'transport', color: '#3B82F6' },
  { id: 'mobility', name: 'Movilidad', shortName: 'Mov', description: 'Paradas, rutas y ciclovías', category: 'transport', color: '#06B6D4' },
  { id: 'waste', name: 'Residuos', shortName: 'Res', description: 'Contenedores y recolección', category: 'environment', color: '#84CC16' },
  { id: 'environmental', name: 'Ambiental', shortName: 'Amb', description: 'Arbolado y espacios verdes', category: 'environment', color: '#10B981' },
  { id: 'safety', name: 'Seguridad', shortName: 'Seg', description: 'Cámaras y monitoreo', category: 'environment', color: '#F97316' },
  { id: 'urban', name: 'Planificación', shortName: 'Plan', description: 'Zonificación y código urbano', category: 'environment', color: '#8B5CF6' },
  { id: 'social', name: 'Social', shortName: 'Soc', description: 'Equipamiento comunitario', category: 'environment', color: '#EC4899' },
];

const CATEGORY_LABELS: Record<string, string> = {
  core: 'Inventario',
  infrastructure: 'Infraestructura',
  transport: 'Transporte',
  environment: 'Ambiente y Ciudad',
};

/**
 * Props del ModuleDropdown
 */
export interface ModuleDropdownProps {
  onModuleChange?: (moduleId: ModuleId | null) => void;
  /** Callback para abrir DataHubModal desde el menú contextual de acciones */
  onOpenDataHub?: () => void;
}

/**
 * Componente ModuleDropdown
 */
export function ModuleDropdown({ onModuleChange, onOpenDataHub }: ModuleDropdownProps): JSX.Element {
  const { activeModule, availableModules, setActiveModule } = useModuleStore();
  const { isModuleHidden, toggleModuleVisibility } = useModuleVisibilityStore();
  const { getModuleStats } = useModuleStatsStore();
  const { isEditingActive, hasUnsavedChanges } = useEditLock();
  const { projectId } = useProject();

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionsMenuOpen, setActionsMenuOpen] = useState<ModuleId | null>(null);
  const [statsModalModule, setStatsModalModule] = useState<ModuleId | null>(null);
  const [configModalModule, setConfigModalModule] = useState<ModuleId | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const activeModuleData = MODULE_DEFINITIONS.find(m => m.id === activeModule);

  /**
   * Verifica si un módulo está disponible
   */
  const isModuleAvailable = (moduleId: ModuleId): boolean => {
    return availableModules.includes(moduleId);
  };

  /**
   * Filtra módulos por búsqueda
   */
  const filterModules = (modules: ModuleDefinition[]): ModuleDefinition[] => {
    if (!searchQuery.trim()) return modules;
    const query = searchQuery.toLowerCase();
    return modules.filter(m =>
      m.name.toLowerCase().includes(query) ||
      m.shortName.toLowerCase().includes(query) ||
      m.description.toLowerCase().includes(query)
    );
  };

  /**
   * Agrupa módulos por categoría
   */
  const getModulesByCategory = (): Record<string, ModuleDefinition[]> => {
    const filtered = filterModules(MODULE_DEFINITIONS);
    return {
      core: filtered.filter(m => m.category === 'core'),
      infrastructure: filtered.filter(m => m.category === 'infrastructure'),
      transport: filtered.filter(m => m.category === 'transport'),
      environment: filtered.filter(m => m.category === 'environment'),
    };
  };

  /**
   * Maneja selección de módulo
   */
  const handleSelectModule = (moduleId: ModuleId): void => {
    if (isEditingActive && hasUnsavedChanges) {
      alert('Debe guardar o cancelar los cambios antes de cambiar de módulo.');
      return;
    }

    if (!isModuleAvailable(moduleId)) return;

    const newModuleId = activeModule === moduleId ? null : moduleId;
    setActiveModule(newModuleId);
    onModuleChange?.(newModuleId);
    setIsOpen(false);
    setSearchQuery('');
    setActionsMenuOpen(null);
  };

  /**
   * Toggle dropdown principal
   */
  const handleToggle = (): void => {
    if (isEditingActive && hasUnsavedChanges) {
      alert('Debe guardar o cancelar los cambios antes de cambiar de módulo.');
      return;
    }
    setIsOpen(!isOpen);
    setActionsMenuOpen(null);
    if (!isOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  };

  /**
   * Toggle menú de acciones de un módulo
   */
  const handleActionsClick = (e: MouseEvent, moduleId: ModuleId): void => {
    e.stopPropagation();
    setActionsMenuOpen(actionsMenuOpen === moduleId ? null : moduleId);
  };

  /**
   * Acción: Ver estadísticas
   */
  const handleViewStats = (e: MouseEvent, moduleId: ModuleId): void => {
    e.stopPropagation();
    setStatsModalModule(moduleId);
    setActionsMenuOpen(null);
  };

  /**
   * Acción: Toggle visibilidad
   */
  const handleToggleVisibility = (e: MouseEvent, moduleId: ModuleId): void => {
    e.stopPropagation();
    toggleModuleVisibility(moduleId);
    // No cerrar menú para permitir múltiples toggles
  };

  /**
   * Acción: Abrir configuración
   */
  const handleOpenConfig = (e: MouseEvent, moduleId: ModuleId): void => {
    e.stopPropagation();
    setConfigModalModule(moduleId);
    setActionsMenuOpen(null);
    setIsOpen(false);
  };

  /**
   * Acción: Abrir DataHub (Importar / Exportar datos)
   */
  const handleOpenDataHub = (e: MouseEvent): void => {
    e.stopPropagation();
    setActionsMenuOpen(null);
    setIsOpen(false);
    onOpenDataHub?.();
  };

  /**
   * Cierra dropdown y menús al hacer click fuera
   */
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: globalThis.MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
        setActionsMenuOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  /**
   * Maneja teclas
   */
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Escape') {
      if (actionsMenuOpen) {
        setActionsMenuOpen(null);
      } else {
        setIsOpen(false);
        setSearchQuery('');
      }
    }
  };

  const modulesByCategory = getModulesByCategory();
  const hasResults = Object.values(modulesByCategory).some(arr => arr.length > 0);

  // Datos para modal de estadísticas
  const statsModalModuleData = statsModalModule
    ? MODULE_DEFINITIONS.find(m => m.id === statsModalModule)
    : null;
  const moduleStatsData = statsModalModule ? getModuleStats(statsModalModule) : null;

  return (
    <div className="module-dropdown-container" ref={containerRef}>
      {/* Botón principal */}
      <button
        type="button"
        className={`module-dropdown-trigger ${isOpen ? 'open' : ''} ${activeModule ? 'has-module' : ''}`}
        onClick={handleToggle}
        disabled={isEditingActive && hasUnsavedChanges}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={activeModuleData ? `Módulo: ${activeModuleData.name}` : 'Seleccionar módulo'}
      >
        <span className="module-dropdown-icon">
          {activeModule && MODULE_ICONS[activeModule] ? MODULE_ICONS[activeModule] : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
          )}
        </span>
        <span className="module-dropdown-label">
          {activeModuleData?.name || 'Módulo'}
        </span>
        <span className="module-dropdown-chevron">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="module-dropdown-menu" role="listbox">
          {/* Búsqueda */}
          <div className="module-dropdown-search">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              className="module-dropdown-search-input"
              placeholder="Buscar módulo..."
              value={searchQuery}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>

          {/* Lista de módulos por categoría */}
          <div className="module-dropdown-list">
            {hasResults ? (
              Object.entries(modulesByCategory).map(([category, modules]) => {
                if (modules.length === 0) return null;
                return (
                  <div key={category} className="module-dropdown-category">
                    <div className="module-dropdown-category-label">
                      {CATEGORY_LABELS[category]}
                    </div>
                    {modules.map(module => {
                      const isHidden = isModuleHidden(module.id);
                      const hasActionsOpen = actionsMenuOpen === module.id;

                      return (
                        <div key={module.id} className="module-dropdown-item-wrapper">
                          <button
                            type="button"
                            className={`module-dropdown-item ${activeModule === module.id ? 'active' : ''} ${!isModuleAvailable(module.id) ? 'disabled' : ''} ${isHidden ? 'hidden-module' : ''}`}
                            onClick={() => handleSelectModule(module.id)}
                            disabled={!isModuleAvailable(module.id)}
                            role="option"
                            aria-selected={activeModule === module.id}
                          >
                            <span
                              className="module-dropdown-item-icon"
                              style={{ color: module.color, opacity: isHidden ? 0.4 : 1 }}
                            >
                              {MODULE_ICONS[module.id]}
                            </span>
                            <span className="module-dropdown-item-text">
                              <span className="module-dropdown-item-name">{module.name}</span>
                              <span className="module-dropdown-item-desc">{module.description}</span>
                            </span>
                            {activeModule === module.id && (
                              <span className="module-dropdown-item-check">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              </span>
                            )}
                          </button>

                          {/* Botón de acciones */}
                          <button
                            type="button"
                            className={`module-actions-btn ${hasActionsOpen ? 'open' : ''}`}
                            onClick={(e) => handleActionsClick(e, module.id)}
                            aria-label={`Acciones de ${module.name}`}
                            aria-haspopup="menu"
                            aria-expanded={hasActionsOpen}
                          >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="12" cy="6" r="2" />
                              <circle cx="12" cy="12" r="2" />
                              <circle cx="12" cy="18" r="2" />
                            </svg>
                          </button>

                          {/* Menú de acciones */}
                          {hasActionsOpen && (
                            <div className="module-actions-menu">
                              <button
                                type="button"
                                className="module-actions-menu-item"
                                onClick={(e) => handleViewStats(e, module.id)}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <line x1="12" y1="20" x2="12" y2="10" />
                                  <line x1="18" y1="20" x2="18" y2="4" />
                                  <line x1="6" y1="20" x2="6" y2="16" />
                                </svg>
                                <span>Ver estadísticas</span>
                              </button>
                              <button
                                type="button"
                                className="module-actions-menu-item"
                                onClick={(e) => handleToggleVisibility(e, module.id)}
                              >
                                {isHidden ? (
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                  </svg>
                                ) : (
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                    <line x1="1" y1="1" x2="23" y2="23" />
                                  </svg>
                                )}
                                <span>{isHidden ? 'Mostrar módulo' : 'Ocultar módulo'}</span>
                              </button>
                              <button
                                type="button"
                                className="module-actions-menu-item"
                                onClick={(e) => handleOpenConfig(e, module.id)}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="3" />
                                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                                </svg>
                                <span>Configuración</span>
                              </button>

                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })
            ) : (
              <div className="module-dropdown-empty">
                No se encontraron módulos
              </div>
            )}
          </div>

          {/* Footer: Gestionar datos */}
          {onOpenDataHub && (
            <div className="module-dropdown-footer">
              <button
                type="button"
                className="module-dropdown-footer-action"
                onClick={(e) => handleOpenDataHub(e)}
              >
                <span className="module-dropdown-footer-action-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <ellipse cx="12" cy="5" rx="9" ry="3" />
                    <path d="M21 12c0 1.657-4.03 3-9 3s-9-1.343-9-3" />
                    <path d="M3 5v14c0 1.657 4.03 3 9 3s9-1.343 9-3V5" />
                  </svg>
                </span>
                <span className="module-dropdown-footer-action-text">
                  <span className="module-dropdown-footer-action-label">Gestionar datos</span>
                  <span className="module-dropdown-footer-action-desc">Importar y exportar información del proyecto</span>
                </span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modal de estadísticas */}
      {statsModalModule && statsModalModuleData && (
        <div className="module-stats-overlay" onClick={() => setStatsModalModule(null)}>
          <div className="module-stats-modal" onClick={(e) => e.stopPropagation()}>
            <div className="module-stats-header">
              <h2 className="module-stats-title">
                Estadísticas - {statsModalModuleData.name}
              </h2>
              <button
                type="button"
                className="module-stats-close"
                onClick={() => setStatsModalModule(null)}
                aria-label="Cerrar estadísticas"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="module-stats-content">
              <div className="stats-section">
                <h3 className="stats-section-title">Elementos cargados</h3>
                <div className="stats-grid">
                  <div className="stat-item">
                    <span className="stat-label">Total:</span>
                    <span className="stat-value">{moduleStatsData?.totalElements || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Tipos:</span>
                    <span className="stat-value">
                      {moduleStatsData ? Object.keys(moduleStatsData.elementTypes).length : 0}
                    </span>
                  </div>
                </div>
                {moduleStatsData && moduleStatsData.totalElements > 0 && (
                  <div className="stats-breakdown">
                    <h4 className="stats-breakdown-title">Desglose por capa:</h4>
                    {Object.values(moduleStatsData.elementTypes).map((typeStats) => (
                      <div key={typeStats.typeName} className="stats-breakdown-item">
                        <span>{typeStats.typeName}</span>
                        <span>{typeStats.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {(!moduleStatsData || moduleStatsData.totalElements === 0) && (
                <p className="stats-placeholder">
                  No hay elementos cargados en este módulo
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de configuración */}
      {configModalModule && projectId && (
        <ModuleConfigModal
          moduleId={configModalModule}
          projectId={projectId}
          isOpen={true}
          onClose={() => setConfigModalModule(null)}
          moduleName={MODULE_DEFINITIONS.find(m => m.id === configModalModule)?.name || 'Módulo'}
        />
      )}
    </div>
  );
}
